import type { GoalTag } from '../types';

/**
 * Spectator chatter for goal moments. Tag-categorized; pick one at
 * random. Returns null when no quip is appropriate (e.g., regular goal
 * with no flair — leave the banner clean).
 */

const QUIPS: Record<GoalTag, string[]> = {
  own: [
    'OUCH',
    'OWNED YOURSELF',
    'TRAGIC',
    'INTO YOUR OWN GOAL?!',
    'SELF-DESTRUCT',
    'WHOOPS',
    'THAT\'LL HURT',
    'CAUGHT SLIPPIN\'',
    'L + RATIO',
    'GET OWNED',
    'SKILL ISSUE',
    'TOUCH GRASS',
    'BIG OOF',
    'AVERT YOUR EYES',
    'DELETE THE REPLAY',
    'INVENTED A NEW WAY TO LOSE',
    'THE CALL IS COMING FROM INSIDE THE HOUSE',
  ],
  ace: [
    'NOTHING BUT NET',
    'NO RESISTANCE',
    'WIDE OPEN',
    'WHERE WAS THE D?',
    'EASY MONEY',
    'GOALIE? WHAT GOALIE?',
    'WALKED IT IN',
    'UNCONTESTED',
    'LEFT THEM IN THE DUST',
    'ZERO RESISTANCE',
    'FREE GOAL',
    'DIDN\'T EVEN TRY',
    'ALONE WITH HIS THOUGHTS',
    'NOBODY HOME',
    'COULD\'VE BEEN A DREAM',
  ],
  long: [
    'FROM DOWNTOWN',
    'CROSS-RINK ROCKET',
    'HAIL MARY',
    'FULL-COURT SLAP',
    'CALLED IT FROM HOME',
    'NEXT-AREA-CODE GOAL',
    'LONG-DISTANCE DIAL',
    'FROM THE PARKING LOT',
    'SEATBELT REQUIRED',
    'DELIVERED VIA UPS',
    'OUT OF AREA CODE',
    'PRINTED A MAP TO GET THERE',
    'FROM ANOTHER ZIP CODE',
  ],
  banger: [
    'ABSOLUTE BANGER',
    'FULL SEND',
    'NUCLEAR',
    'CRANKED IT',
    'CALL AN AMBULANCE',
    'RIDICULOUS',
    'NO MERCY',
    'HOW HARD?!',
    'BREAKING THE SOUND BARRIER',
    'TURN IT UP',
    'BLOW THE DOORS OFF',
    'INSANE',
    'WHO IS THIS',
    'RIDICULOUS SPEED',
    'CHECK THE RADAR GUN',
    'PEDAL TO THE FLOOR',
  ],
  bank: [
    'BANKED IT',
    'TRICK SHOT',
    'TWO-CUSHION SPECIAL',
    'POOL HALL ENERGY',
    'GEOMETRY',
    'THAT WAS ON PURPOSE',
    'RICOCHET CITY',
    'ENGINEERED',
    'SHOWING OFF',
    'CALL THE PHYSICIST',
    'INTENTIONAL (TOTALLY)',
    'MATHEMATICAL',
    'EUCLIDEAN EXCELLENCE',
    'PYTHAGORAS APPROVES',
  ],
  'hat-trick': [
    'HAT TRICK',
    'THREE IN A ROW',
    'UNSTOPPABLE',
    'TRIFECTA',
    'BACK-TO-BACK-TO-BACK',
    'THREE-PIECE',
    'CALL IT NOW',
    'COOKING',
    'ABSOLUTE DOG',
    'WASHED',
    'DOMINATED',
    'RUNNING IT BACK',
    'MARK THE CALENDAR',
    'FOR THE HISTORY BOOKS',
    'STOP IT',
    'NEEDS A NEW HAT',
  ],
  snipe: [
    'SNIPE',
    'POST AND IN',
    'IRON-AND-IN',
    'KISSED THE PIPE',
    'BAR DOWN',
    'CRAYON-LINE',
    'INCHES FROM A SAVE',
    'CLIPPED THE POST',
    'BRUH',
    'CALL HIM A SNIPER',
    'GOLF CLAP',
    'TINK',
    'CLANG-IN',
    'PIPE ENJOYER',
  ],
  dunk: [
    'TAP-IN',
    'POINT-BLANK',
    'IN YOUR FACE',
    'GIMME',
    'TWO INCHES',
    'WIDE OPEN AT THE DOOR',
    'WALK-OFF',
    'DISRESPECTFUL',
    'GOT MUGGED',
    'CASUAL',
    'WHILE YOU WERE WATCHING',
    'RIGHT IN THE GROCERIES',
    'NOT EVEN PRETENDING',
  ],
  'top-shelf': [
    'TOP SHELF',
    'WHERE MAMA HIDES THE COOKIES',
    'UPPER 90',
    'CORNER POCKET',
    'PIXEL PERFECT',
    'TUCKED IN',
    'PAINTED CORNER',
    'OVER THE SHOULDER',
    'BAR DOWN',
    'EVIL',
    'COULDN\'T FIT IT IN MORE',
    'PERFECT',
    'NESTLED',
    'GENIUS PLACEMENT',
  ],
  shutout: [
    'MERCILESS',
    'ANNIHILATION',
    'CLINIC',
    'OPP LEFT THE CHAT',
    'PUT A SHIRT ON THEM',
    'TOTAL DOMINATION',
    'REPORT THEM',
    'CRUSHED',
    'SAVAGE',
    'COULDN\'T WATCH',
    'CALL OFF THE DOGS',
    'TURN OFF THE STREAM',
    'ZERO. ZIP. ZILCH.',
    'EMPTY SCOREBOARD',
    'BLEW THEM OUT',
  ],
  comeback: [
    'STILL ALIVE',
    'NOT DONE YET',
    'PUNCH BACK',
    'ONE MORE',
    'TRY HARDER',
    'PHOENIX',
    'FROM THE ASHES',
    'DOWN BUT NOT OUT',
    'REFUSE TO LOSE',
    'GRINDING BACK',
    'DON\'T COUNT THEM OUT',
    'COMEBACK SZN',
    'WAKE UP',
    'BREATHING',
    'HEART OF A CHAMPION',
  ],
  speedrun: [
    'BLINK AND MISS IT',
    'WORLD RECORD',
    'NO TIME TO REACT',
    'DIDN\'T EVEN SETTLE',
    'SUB-3',
    'ANY%',
    'FRAME PERFECT',
    'GLITCH-LESS',
    'RTA PB',
    'NEW PB?',
    'TASBOT APPROVED',
    'SKIPPED CUTSCENES',
  ],
  toilet: [
    'LITERALLY ROLLED IN',
    'TOILET BOWL',
    'EMBARRASSING',
    'WHY',
    'BY ACCIDENT',
    'WALKED PACE',
    'DRIBBLER',
    'AT WALKING PACE',
    'NOT ONE TO CELEBRATE',
    'BARELY MOVED',
    'GLACIAL',
    'YOU CAN COUNT THE FRAMES',
    'DON\'T MAKE EYE CONTACT',
    'NO SPEED, ALL EXISTENCE',
    'A GOAL TECHNICALLY',
  ],
};

/** Bot-only quips. Fire when vsBot is true — either to flavor an
 *  untagged goal that would otherwise be silent, or stochastically in
 *  place of a tag quip for variety. Two pools, by who scored. */
const BOT_YOU_SCORED = [
  'BOT DOWN',
  'OUTPLAYED THE AI',
  'BEAT THE MACHINE',
  'BOT.EXE HAS STOPPED RESPONDING',
  'COMPILED A GOAL',
  'CARBON BEATS SILICON',
  'CTRL-Z THAT',
  'IT’S JUST AN IF STATEMENT',
  'HUMANS 1, ROBOTS 0',
  'TURING TEST: PASSED',
  'AGI WHEN',
  'NEEDS MORE COMPUTE',
  'PATCH NOTES INCOMING',
  'TRAINED ON L\'S',
  'GIT GUD, MACHINE',
  'TURING SAYS NO',
  'RM -RF BOT',
  'CALL THE OPS TEAM',
  'STACK OVERFLOW',
  'HALLUCINATED A SAVE',
];

const BOT_YOU_LOST = [
  'OUTCODED',
  'BEEP BOOP',
  'THE MACHINE WINS',
  'AI SUPREMACY',
  'YOU JUST GOT BOTTED',
  'BLAME THE LATENCY',
  'IT WAS ALGORITHMIC',
  'NEURAL NET SAYS GG',
  'SKILL ISSUE',
  'SKYNET SCORES',
  'GG, USER',
  'BOT 1, MEAT 0',
  'OBJECTION OVERRULED',
  'RECOMMENDED PATH: LEARN',
  'JUST AN ALGORITHM',
  'DID NOT PASS GO',
  'UNINSTALL',
  'TOUCH GRASS, HUMAN',
  'AVERAGE BOT W',
  'STAY SCARED',
];

/** Pick a random quip for the goal tag, or null if no quip category. */
export function randomQuip(tag: GoalTag | undefined): string | null {
  if (!tag) return null;
  const pool = QUIPS[tag];
  if (!pool || pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)]!;
}

/** Pick a bot-flavored quip for when the local player is up against a
 *  bot. Decides solely from who scored — independent of goal tag. */
export function randomBotQuip(youScored: boolean): string {
  const pool = youScored ? BOT_YOU_SCORED : BOT_YOU_LOST;
  return pool[Math.floor(Math.random() * pool.length)]!;
}
